import { computed, onBeforeUnmount, ref, watch } from 'vue';
import { useQuery } from '@vue/apollo-composable';
import { MY_SCHOOLS } from '@/graphql';
import type { School, SchoolPaginator } from '@/types/composition-functions';

export function useSchoolPickerQuery() {
  const search = ref('');
  const debouncedSearch = ref('');
  const currentPage = ref(1);
  const schools = ref<School[]>([]);
  const error = ref('');
  let timer: ReturnType<typeof setTimeout> | undefined;
  const variables = computed(() => ({
    search: debouncedSearch.value || undefined, first: 20, page: currentPage.value,
  }));
  const { result, loading, onError, refetch } = useQuery<{ mySchools: SchoolPaginator }>(MY_SCHOOLS, variables, {
    fetchPolicy: 'network-only',
  });
  watch(search, value => {
    clearTimeout(timer);
    timer = setTimeout(() => {
      if (value.trim() === debouncedSearch.value) return;
      schools.value = [];
      error.value = '';
      currentPage.value = 1;
      debouncedSearch.value = value.trim();
    }, 300);
  });
  onBeforeUnmount(() => clearTimeout(timer));
  watch(result, value => {
    if (!value?.mySchools?.data) return;
    error.value = '';
    const incoming = value.mySchools.data;
    const existing = currentPage.value === 1 ? [] : schools.value;
    const ids = new Set(existing.map(school => school.id));
    schools.value = [...existing, ...incoming.filter(school => !ids.has(school.id))];
  });
  onError(() => { error.value = 'Your schools could not be loaded. Please try again.'; });
  const hasMore = computed(() => result.value?.mySchools?.paginatorInfo?.hasMorePages ?? false);
  const total = computed(() => result.value?.mySchools?.paginatorInfo?.total);
  function loadMore() {
    if (!loading.value && hasMore.value && !error.value) currentPage.value++;
  }
  async function retry() {
    error.value = '';
    try { await refetch(); } catch { error.value = 'Your schools could not be loaded. Please try again.'; }
  }
  return { search, schools, loading, error, hasMore, total, loadMore, retry };
}
